import React, { useContext, useEffect, useState, useMemo } from "react";
import {
  Typography,
  CardContent,
  Card,
  Grid,
  Divider,
  CircularProgress,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import Plotting from "../Plotting";
import { fetchData } from "../../utils/api";
import { UserContext } from "../../App";
import RangeSelector from "../UIComponents/RangeSelector";

const RANGE_THRESHOLD = 100; // Threshold for showing range selector

export default function AnalyzeErrorModel(props) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { datasets, currentDatasetIdx, experiments, currentExperimentIdx } =
    useContext(UserContext);

  const [errorModelStats, setErrorModelStats] = useState(null);
  const [range, setRange] = useState({ start: 0, end: 1000 });
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [totalAvailablePoints, setTotalAvailablePoints] = useState(null);
  const [error, setError] = useState(null);

  const datasetName = useMemo(
    () => datasets?.[currentDatasetIdx]?.datasetName,
    [datasets, currentDatasetIdx]
  );
  const experimentName = useMemo(
    () => experiments?.[currentExperimentIdx]?.experimentName,
    [experiments, currentExperimentIdx]
  );

  const runName = useMemo(
    () => props?.runName || props?.runData?.runName,
    [props?.runName, props?.runData]
  );
  const selectedTarget = useMemo(
    () => props?.selectedTarget,
    [props?.selectedTarget]
  );

  const isReady = datasetName && experimentName && runName;

  const fetchErrorModelData = async (useManualRange = false) => {
    if (!isReady) {
      console.warn("Required parameters are missing. Skipping fetch.");
      return;
    }

    if (
      useManualRange &&
      (range.start === "" ||
        range.end === "" ||
        Number.isNaN(range.start) ||
        Number.isNaN(range.end) ||
        range.start < 0 ||
        range.end <= range.start)
    ) {
      alert("Please enter a valid start and end range (start < end).");
      return;
    }

    const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v0/dataset/${datasetName}/experiment/${experimentName}/run/${runName}/predictions`;
    const params = new URLSearchParams();
    if (useManualRange) {
      params.set("start", range.start);
      params.set("end", range.end);
    }
    if (selectedTarget) {
      params.set("target", selectedTarget);
    }
    const url = params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;

    try {
      const response = await fetchData(url);
      setErrorModelStats({ predictionDetails: response });
      setIsDataLoaded(true);
      setError(null);

      if (!totalAvailablePoints || !useManualRange) {
        const fullLength = Array.isArray(response?.timestamps)
          ? response.timestamps.length
          : response?.totalPoints || 0;
        setTotalAvailablePoints(fullLength);
      }
    } catch (err) {
      setError("Failed to load error model data.");
      setIsDataLoaded(true);
    }
  };
  const isTimeSeriesData =
    isDataLoaded &&
    (Array.isArray(errorModelStats?.predictionDetails?.timestamps) ||
      errorModelStats?.predictionDetails?.timestamps.length >= 0);

  const renderPlotCard = (plotType) => {
    if (!isDataLoaded) {
      return (
        <Card>
          <CircularProgress />
        </Card>
      );
    }
    if (error) {
      return (
        <Card>
          <Typography color="error" sx={{ p: 2 }}>
            {error}
          </Typography>
        </Card>
      );
    }
    return (
      <Card>
        <Plotting runData={errorModelStats} type={plotType} />
      </Card>
    );
  };

  const renderDescriptionCard = (titleKey, descriptionKey) => (
    <Card sx={{ backgroundColor: theme.palette.background.default }}>
      <CardContent>
        <Typography gutterBottom variant="h5" component="div">
          {t(titleKey)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(descriptionKey)}
        </Typography>
      </CardContent>
    </Card>
  );
  const handleReset = () => {
    setRange({ start: 0, end: 1000 });
    fetchErrorModelData(false);
  };

  useEffect(() => {
    if (isReady) {
      setIsDataLoaded(false);
      fetchErrorModelData(range.start !== 0 || range.end !== 1000);
    }
  }, [isReady, selectedTarget]);

  if (!props.runData?.predictionDetails?.targetListTrain) {
    return null;
  }

  return (
    <Grid item xl={12} xs={12} sx={{ marginTop: theme.spacing(4) }}>
      <Grid container spacing={2}>
        {totalAvailablePoints > RANGE_THRESHOLD && (
          <RangeSelector
            range={range}
            setRange={setRange}
            onLoad={() => fetchErrorModelData(true)}
            onReset={handleReset}
          />
        )}

        {isTimeSeriesData ? (
          <>
            <Grid item lg={8} xs={12}>
              {renderPlotCard("errorModel")}
            </Grid>
            <Grid item lg={4} xs={12}>
              {renderDescriptionCard(
                "analyzer.ErrorModelPlot",
                "analyzer.DescriptionErrorModelPlot"
              )}
            </Grid>
            <Grid item lg={8} xs={12}>
              {renderPlotCard("errorScatter")}
            </Grid>
            <Grid item lg={4} xs={12}>
              {renderDescriptionCard(
                "analyzer.ErrorModelScatterPlot",
                "analyzer.DescriptionErrorModelScatterPlot"
              )}
            </Grid>
          </>
        ) : (
          <>
            <Grid item lg={8} xs={12}>
              {renderPlotCard("errorScatter")}
            </Grid>
            <Grid item lg={4} xs={12}>
              {renderDescriptionCard(
                "analyzer.ErrorModelScatterPlot",
                "analyzer.DescriptionErrorModelScatterPlot"
              )}
            </Grid>
          </>
        )}
      </Grid>
      <Divider textAlign="left" />
    </Grid>
  );
}
